import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { ArrowRight, Bot, Loader2, Mic, MicOff, Sparkles } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { CurrencyInput } from "@/components/ui/currency-input";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Category, Conta } from "@/types/database";
import { ParsedFinancialCommand, parseFinancialCommand } from "@/lib/assistant-parser";
import {
  AssistantExecutionMode,
  AssistantParseSource,
  buildAssistantTransactionDraft,
  detectAssistantCapabilities,
  inferAssistantExecutionMode,
  interpretCommandWithFallback,
} from "@/lib/assistant-orchestrator";
import { tryParseWithLocalModel } from "@/lib/assistant-local-model";
import { useIsMobile } from "@/hooks/use-mobile";

interface LocalAIAssistantProps {
  onTransactionSuccess: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  showTrigger?: boolean;
  autoStartListeningOnOpen?: boolean;
  onAutoStartListeningHandled?: () => void;
}

interface SpeechRecognitionResultEvent extends Event {
  resultIndex?: number;
  results: {
    [key: number]: {
      isFinal?: boolean;
      [key: number]: {
        transcript: string;
      };
    };
  };
}

interface SpeechRecognitionErrorEvent extends Event {
  error?: string;
}

interface SpeechRecognitionLike extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: SpeechRecognitionResultEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
}

interface SpeechRecognitionWindow extends Window {
  SpeechRecognition?: new () => SpeechRecognitionLike;
  webkitSpeechRecognition?: new () => SpeechRecognitionLike;
}

export function LocalAIAssistant({
  onTransactionSuccess,
  open: controlledOpen,
  onOpenChange,
  showTrigger = true,
  autoStartListeningOnOpen = false,
  onAutoStartListeningHandled,
}: LocalAIAssistantProps) {
  const VOICE_SILENCE_TIMEOUT_MS = 1800;
  const { user } = useAuth();
  const [internalOpen, setInternalOpen] = useState(false);
  const [input, setInput] = useState("");
  const [parsed, setParsed] = useState<ParsedFinancialCommand | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [accounts, setAccounts] = useState<Conta[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [listening, setListening] = useState(false);
  const [tipo, setTipo] = useState<"entrada" | "saida">("saida");
  const [valor, setValor] = useState<number | string>("");
  const [descricao, setDescricao] = useState("");
  const [categoriaId, setCategoriaId] = useState("none");
  const [contaId, setContaId] = useState("");
  const [data, setData] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [executionMode, setExecutionMode] = useState<AssistantExecutionMode>("rule-only");
  const [parseSource, setParseSource] = useState<AssistantParseSource>("rule-parser");
  const [interpreting, setInterpreting] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const voiceTranscriptRef = useRef("");
  const voiceSilenceTimeoutRef = useRef<number | null>(null);
  const autoStartTriggeredRef = useRef(false);
  const isOpenControlled = controlledOpen !== undefined;
  const open = isOpenControlled ? controlledOpen : internalOpen;

  const setOpen = useCallback((nextOpen: boolean) => {
    if (!isOpenControlled) {
      setInternalOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  }, [isOpenControlled, onOpenChange]);

  const clearVoiceSilenceTimeout = useCallback(() => {
    if (voiceSilenceTimeoutRef.current) {
      window.clearTimeout(voiceSilenceTimeoutRef.current);
      voiceSilenceTimeoutRef.current = null;
    }
  }, []);

  const mapVoiceErrorMessage = useCallback((errorCode?: string) => {
    if (errorCode === "not-allowed" || errorCode === "service-not-allowed") {
      return "Permissão do microfone negada no navegador";
    }
    if (errorCode === "aborted") {
      return "Captura de voz interrompida";
    }
    if (errorCode === "network") {
      return "Erro de rede no reconhecimento de voz";
    }
    if (errorCode === "no-speech") {
      return "Não detectei fala. Tente novamente falando logo após clicar.";
    }
    if (errorCode === "audio-capture") {
      return "Não foi possível acessar o microfone";
    }
    return "Falha ao capturar voz";
  }, []);

  const speechSupported = useMemo(() => {
    const speechWindow = window as SpeechRecognitionWindow;
    return Boolean(speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition);
  }, []);
  const isMobile = useIsMobile();

  const normalizeTranscript = useCallback((value: string) => {
    return value.replace(/\s+/g, " ").trim();
  }, []);

  const mergeTranscriptChunks = useCallback((base: string, chunk: string) => {
    const normalizedBase = normalizeTranscript(base);
    const normalizedChunk = normalizeTranscript(chunk);
    if (!normalizedBase) return normalizedChunk;
    if (!normalizedChunk) return normalizedBase;
    if (normalizedBase === normalizedChunk) return normalizedBase;
    if (normalizedChunk.toLowerCase().startsWith(normalizedBase.toLowerCase())) return normalizedChunk;
    if (normalizedBase.toLowerCase().endsWith(normalizedChunk.toLowerCase())) return normalizedBase;

    const maxOverlap = Math.min(normalizedBase.length, normalizedChunk.length);
    for (let size = maxOverlap; size > 0; size -= 1) {
      const baseSuffix = normalizedBase.slice(-size).toLowerCase();
      const chunkPrefix = normalizedChunk.slice(0, size).toLowerCase();
      if (baseSuffix === chunkPrefix) {
        return normalizeTranscript(`${normalizedBase} ${normalizedChunk.slice(size)}`);
      }
    }

    return normalizeTranscript(`${normalizedBase} ${normalizedChunk}`);
  }, [normalizeTranscript]);

  useEffect(() => {
    const capabilities = detectAssistantCapabilities(window, navigator);
    const mode = inferAssistantExecutionMode(capabilities);
    setExecutionMode(mode);
  }, []);

  useEffect(() => {
    if (!open || !user?.id) return;

    const load = async () => {
      setLoadingData(true);
      try {
        const [{ data: categoriesData, error: categoriesError }, { data: accountsData, error: accountsError }] = await Promise.all([
          supabase.from("categorias").select("*").eq("user_id", user.id).order("nome"),
          supabase.from("contas").select("*").eq("user_id", user.id).eq("ativo", true).order("nome"),
        ]);

        if (categoriesError) throw categoriesError;
        if (accountsError) throw accountsError;

        const loadedAccounts = (accountsData as Conta[]) || [];
        const loadedCategories = (categoriesData as Category[]) || [];

        setAccounts(loadedAccounts);
        setCategories(loadedCategories);

        if (!contaId && loadedAccounts.length > 0) {
          const defaultAccount = loadedAccounts.find((account) => account.tipo !== "cartao_credito") ?? loadedAccounts[0];
          if (defaultAccount) setContaId(defaultAccount.id);
        }
      } catch (error) {
        toast.error("Erro ao carregar dados da assistente");
      } finally {
        setLoadingData(false);
      }
    };

    load();
  }, [open, user?.id, contaId]);

  useEffect(() => {
    if (!open && recognitionRef.current) {
      clearVoiceSilenceTimeout();
      recognitionRef.current.stop();
      recognitionRef.current = null;
      setListening(false);
    }
  }, [open, clearVoiceSilenceTimeout]);

  const categoriesByTipo = useMemo(() => {
    return categories.filter((category) => category.tipo === "ambos" || category.tipo === tipo);
  }, [categories, tipo]);
  const categoryNames = useMemo(() => categories.map((category) => category.nome), [categories]);
  const accountNames = useMemo(() => accounts.map((account) => account.nome), [accounts]);

  const applyParsedResult = useCallback((result: ParsedFinancialCommand, source: AssistantParseSource) => {
    const draft = buildAssistantTransactionDraft({
      parsed: result,
      categories,
      accounts,
      nowDate: format(new Date(), "yyyy-MM-dd"),
      source,
    });

    setTipo(draft.tipo);
    setValor(draft.valor);
    setDescricao(draft.descricao);
    setData(draft.data);
    setCategoriaId(draft.categoriaId);
    setParseSource(draft.source);
    if (draft.contaId) {
      setContaId(draft.contaId);
    }
  }, [accounts, categories]);

  const interpretText = useCallback(async (text: string, silentSuccess: boolean = false) => {
    if (!text.trim()) {
      toast.error("Digite ou fale um comando para interpretar");
      return;
    }

    setInterpreting(true);
    try {
      const interpretation = await interpretCommandWithFallback({
        input: text,
        mode: executionMode,
        parseRule: (rawText) => parseFinancialCommand(rawText, { categoryNames, accountNames }),
        parseLocalModel: (rawText) => tryParseWithLocalModel(rawText, { categoryNames, accountNames }),
      });

      if (!interpretation) {
        setParsed(null);
        toast.error("Não consegui interpretar esse comando");
        return;
      }

      setParsed(interpretation.parsed);
      applyParsedResult(interpretation.parsed, interpretation.source);
      if (!silentSuccess) {
        toast.success("Comando interpretado. Revise e confirme.");
      }
    } finally {
      setInterpreting(false);
    }
  }, [applyParsedResult, categoryNames, accountNames, executionMode]);

  const forceStopRecognition = useCallback(() => {
    let currentTranscript = "";
    
    try {
      if (recognitionRef.current) {
        currentTranscript = voiceTranscriptRef.current.trim();
        
        recognitionRef.current.onend = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onresult = null;

        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
    } catch (e) {
      console.warn("Erro ao forçar parada do reconhecimento de voz", e);
    }

    setListening(false);
    clearVoiceSilenceTimeout();
    
    if (currentTranscript && !interpreting) {
      voiceTranscriptRef.current = "";
      
      if (open) {
        interpretText(currentTranscript, true).catch(console.error);
      }
    }
  }, [clearVoiceSilenceTimeout, interpreting, interpretText, open]);

  // Limpa o estado quando o modal for fechado
  useEffect(() => {
    if (!open) {
      setInput("");
      setParsed(null);
      setValor("");
      setDescricao("");
      setCategoriaId("none");
      setData(format(new Date(), "yyyy-MM-dd"));
      voiceTranscriptRef.current = "";
    }
  }, [open]);

  const handleInterpret = useCallback(async () => {
    await interpretText(input);
  }, [input, interpretText]);

  const handleInputKeyDown = useCallback((event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.nativeEvent.isComposing) {
      return;
    }

    if (event.key !== "Enter" || event.shiftKey) {
      return;
    }

    if (loadingData || interpreting || listening || recognitionRef.current) {
      return;
    }

    event.preventDefault();
    void handleInterpret();
  }, [handleInterpret, interpreting, listening, loadingData]);

  const startVoiceCapture = useCallback(() => {
    if (!speechSupported) {
      toast.error("Reconhecimento de voz não disponível neste navegador");
      return;
    }

    if (listening || recognitionRef.current) {
      return;
    }

    const speechWindow = window as SpeechRecognitionWindow;
    const RecognitionCtor = speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition;
    if (!RecognitionCtor) return;

    const recognition = new RecognitionCtor();
    recognition.lang = "pt-BR";
    recognition.continuous = false;
    recognition.interimResults = true;

    const scheduleVoiceStopBySilence = () => {
      clearVoiceSilenceTimeout();
      voiceSilenceTimeoutRef.current = window.setTimeout(() => {
        recognition.stop();
      }, VOICE_SILENCE_TIMEOUT_MS);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    recognition.onresult = (event: any) => {
      let committedTranscript = voiceTranscriptRef.current;
      let interimTranscript = "";
      let hasFinalChunk = false;
      const startIndex = event.resultIndex ?? 0;

      // O objeto event.results pode não ser um array puro em alguns browsers, 
      // então calculamos o tamanho pegando as chaves numéricas
      const resultsLength = Object.keys(event.results || {}).filter(k => !isNaN(Number(k))).length;
      for (let index = startIndex; index < resultsLength; index += 1) {
        const result = event.results[index] as SpeechRecognitionResult;
        const chunk = result?.[0]?.transcript || "";
        if (!chunk.trim()) continue;
        if (result?.isFinal) {
          committedTranscript = mergeTranscriptChunks(committedTranscript, chunk);
          hasFinalChunk = true;
        } else {
          interimTranscript = mergeTranscriptChunks(interimTranscript, chunk);
        }
      }

      voiceTranscriptRef.current = committedTranscript;
      const transcript = normalizeTranscript(
        committedTranscript ? `${committedTranscript} ${interimTranscript}` : interimTranscript
      );
      if (!transcript) return;
      setInput(transcript);
      if (hasFinalChunk) {
        scheduleVoiceStopBySilence();
      }
    };

    recognition.onerror = (event) => {
      const errorCode = event.error;
      forceStopRecognition();
      if (errorCode === "aborted") return;
      toast.error(mapVoiceErrorMessage(errorCode));
    };

    recognition.onend = async () => {
      clearVoiceSilenceTimeout();
      setListening(false);

      const transcript = voiceTranscriptRef.current.trim();
      voiceTranscriptRef.current = "";

      recognitionRef.current = null;

      if (!transcript) {
        return;
      }

      await interpretText(transcript, true);
    };

    recognitionRef.current = recognition;
    voiceTranscriptRef.current = "";
    clearVoiceSilenceTimeout();
    setListening(true);
    try {
      recognition.start();
    } catch {
      forceStopRecognition();
      toast.error("Não foi possível iniciar o reconhecimento de voz");
    }
  }, [clearVoiceSilenceTimeout, interpretText, listening, mapVoiceErrorMessage, mergeTranscriptChunks, normalizeTranscript, speechSupported, forceStopRecognition]);

  const stopVoiceCapture = useCallback(() => {
    forceStopRecognition();
  }, [forceStopRecognition]);

  const handleVoiceToggle = useCallback(() => {
    if (recognitionRef.current || listening) {
      forceStopRecognition();
    } else {
      startVoiceCapture();
    }
  }, [listening, startVoiceCapture, forceStopRecognition]);

  useEffect(() => {
    if (!open) {
      autoStartTriggeredRef.current = false;
      
      // Quando for fechado e ainda estiver gravando, forçamos a parada sem disparar o submit
      if (recognitionRef.current || listening) {
        forceStopRecognition();
      }
      return;
    }
    if (!autoStartListeningOnOpen) return;
    if (autoStartTriggeredRef.current) return;
    autoStartTriggeredRef.current = true;
    const timer = window.setTimeout(() => {
      startVoiceCapture();
      onAutoStartListeningHandled?.();
    }, 150);
    return () => window.clearTimeout(timer);
  }, [open, autoStartListeningOnOpen, onAutoStartListeningHandled, startVoiceCapture, forceStopRecognition, listening]);

  useEffect(() => {
    if (!parsed || categories.length === 0 || categoriaId !== "none") return;
    applyParsedResult(parsed, parseSource);
  }, [parsed, categories, categoriaId, parseSource, applyParsedResult]);

  const handleSave = async () => {
    if (!user?.id) {
      toast.error("Usuário não autenticado");
      return;
    }

    const numericAmount = Number(valor);
    if (!numericAmount || numericAmount <= 0) {
      toast.error("Informe um valor válido");
      return;
    }

    if (!descricao.trim()) {
      toast.error("Informe uma descrição");
      return;
    }

    if (!contaId) {
      toast.error("Selecione uma conta");
      return;
    }

    setSaving(true);
    try {
      const selectedAccount = accounts.find((account) => account.id === contaId);
      const isCreditCard = selectedAccount?.tipo === "cartao_credito";
      const foiPaga = isCreditCard ? false : true;
      const { error } = await supabase.from("transacoes").insert({
        user_id: user.id,
        tipo,
        valor: numericAmount,
        descricao: descricao.trim(),
        data,
        categoria_id: categoriaId !== "none" ? categoriaId : null,
        conta_id: contaId,
        foi_paga: foiPaga,
        data_pagamento: foiPaga ? data : null,
      });

      if (error) throw error;

      toast.success(tipo === "entrada" ? "Receita criada com sucesso" : "Despesa criada com sucesso");
      onTransactionSuccess();
      setOpen(false);
      setInput("");
      setParsed(null);
      setValor("");
      setDescricao("");
      setCategoriaId("none");
      setData(format(new Date(), "yyyy-MM-dd"));
    } catch (error) {
      toast.error("Erro ao salvar transação pela assistente");
    } finally {
      setSaving(false);
    }
  };

  // Formulário compartilhado entre Sheet (mobile) e Dialog (desktop)
  const formContent = (
    <div className="mt-6 space-y-4">
      <div className="relative rounded-3xl border bg-background px-4 pt-4 pb-16">
        <Textarea
          id="assistant-input"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={handleInputKeyDown}
          placeholder='Ex: "gastei 35 reais no uber"'
          className="min-h-32 resize-none border-0 bg-transparent p-0 pr-16 focus-visible:ring-0 focus-visible:ring-offset-0"
        />
        <div className="absolute inset-x-3 bottom-3 flex items-center justify-between">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleVoiceToggle}
            disabled={!speechSupported}
            className="h-12 w-12 rounded-full"
            aria-label={listening ? "Parar voz" : "Falar"}
          >
            {listening ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
          </Button>
          <Button
            type="button"
            size="icon"
            onClick={handleInterpret}
            disabled={loadingData || interpreting}
            className="h-12 w-12 rounded-full"
            aria-label="Enviar"
          >
            <ArrowRight className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {parsed && (
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={tipo} onValueChange={(value: "entrada" | "saida") => setTipo(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="saida">Despesa</SelectItem>
                    <SelectItem value="entrada">Receita</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Data</Label>
                <Input type="date" value={data} onChange={(event) => setData(event.target.value)} />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Descrição</Label>
              <Input value={descricao} onChange={(event) => setDescricao(event.target.value)} />
            </div>

            <div className="space-y-2">
              <Label>Valor</Label>
              <CurrencyInput value={valor} onChange={setValor} placeholder="0,00" />
            </div>

            <div className="space-y-2">
              <Label>Categoria</Label>
              <Select value={categoriaId} onValueChange={setCategoriaId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecionar categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem categoria</SelectItem>
                  {categoriesByTipo.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Conta</Label>
              <Select value={contaId} onValueChange={setContaId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecionar conta" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.tipo === "cartao_credito" ? `Cartão • ${account.nome}` : account.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button type="button" className="w-full" onClick={handleSave} disabled={saving || loadingData}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Confirmar e salvar
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );

  return (
    <>
      {showTrigger && (
        <Button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed right-4 bottom-24 z-40 h-12 w-12 rounded-full shadow-lg md:right-6 md:bottom-6"
          size="icon"
        >
          <Bot className="h-5 w-5" />
        </Button>
      )}

      {isMobile ? (
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="bottom" className="h-[88vh] overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Assistente Financeiro
              </SheetTitle>
            </SheetHeader>
            {formContent}
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Assistente Financeiro
              </DialogTitle>
            </DialogHeader>
            {formContent}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
